import React, { useState, useEffect } from 'react';
import { usersAPI, playersAPI, ageGroupsAPI } from '../../services/api';
import Modal from '../ui/Modal';
import PlayerPicker from '../players/PlayerPicker';
import { AgeGroupSelect as AgeGroupSelectType } from '../../types/ageGroup';
import { PASSWORD_MIN_LENGTH, validatePassword } from '../../utils/passwordPolicy';
import { getErrorMessage } from '../../utils/errorMessage';

interface User {
  id: number;
  email: string;
  name: string;
  isAdmin: boolean;
  isCoach: boolean;
  isPlayer: boolean;
  isParent: boolean;
  isGuest: boolean;
  guestTeam?: string | null;
  isActive: boolean;
  ageGroups?: string | null;
  ageGroupIds?: number[];
}

interface Player {
  id: number;
  name: string;
  ageGroup: string;
  userId?: number | null;
  parentUserId?: number | null;
  secondParentUserId?: number | null;
}

interface UserFormProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
  user: User | null;
}

const UserForm: React.FC<UserFormProps> = ({ isOpen, onClose, onSuccess, user }) => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
    // Egyszerre több is bepipálható - pl. egy felnőtt játékos-edző Coach ÉS
    // Player egyszerre.
    isAdmin: false,
    isCoach: false,
    isPlayer: false,
    isParent: false,
    isGuest: false,
    guestTeam: '',
    isActive: true,
    sendWelcomeEmail: false,
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Age groups for Staff (Admin/Coach) users - now using IDs
  const [availableAgeGroups, setAvailableAgeGroups] = useState<AgeGroupSelectType[]>([]);
  const [selectedAgeGroupIds, setSelectedAgeGroupIds] = useState<number[]>([]);
  const [loadingAgeGroups, setLoadingAgeGroups] = useState(false);

  // Player associations
  const [players, setPlayers] = useState<Player[]>([]);
  const [loadingPlayers, setLoadingPlayers] = useState(false);
  const [selectedPlayerId, setSelectedPlayerId] = useState<number | null>(null); // Player képességhez
  const [selectedChildPlayerIds, setSelectedChildPlayerIds] = useState<number[]>([]); // Parent képességhez

  // Load players and age groups when modal opens
  useEffect(() => {
    if (isOpen) {
      loadPlayers();
      loadAgeGroups();
    }
  }, [isOpen]);

  const loadPlayers = async () => {
    setLoadingPlayers(true);
    try {
      const data = await playersAPI.getAll();
      setPlayers(data);
    } catch (err) {
      console.error('Failed to load players:', err);
    } finally {
      setLoadingPlayers(false);
    }
  };

  const loadAgeGroups = async () => {
    setLoadingAgeGroups(true);
    try {
      const data = await ageGroupsAPI.getAll();
      setAvailableAgeGroups(data.map(ag => ({ id: ag.id, name: ag.name })));
    } catch (err) {
      console.error('Failed to load age groups:', err);
    } finally {
      setLoadingAgeGroups(false);
    }
  };

  useEffect(() => {
    if (user) {
      setFormData({
        name: user.name,
        email: user.email,
        password: '',
        isAdmin: user.isAdmin,
        isCoach: user.isCoach,
        isPlayer: user.isPlayer,
        isParent: user.isParent,
        isGuest: user.isGuest,
        guestTeam: user.guestTeam ?? '',
        isActive: user.isActive,
        sendWelcomeEmail: false,
      });

      // Load user's age groups - now using IDs from the new junction table
      if (user.ageGroupIds && user.ageGroupIds.length > 0) {
        setSelectedAgeGroupIds(user.ageGroupIds);
      } else {
        setSelectedAgeGroupIds([]);
      }

      // Find player associations for this user
      if (players.length > 0) {
        const linkedPlayer = players.find(p => p.userId === user.id);
        setSelectedPlayerId(linkedPlayer?.id || null);

        const childPlayers = players.filter(
          p => p.parentUserId === user.id || p.secondParentUserId === user.id
        );
        setSelectedChildPlayerIds(childPlayers.map(p => p.id));
      }
    } else {
      setFormData({
        name: '',
        email: '',
        password: '',
        isAdmin: false,
        isCoach: false,
        isPlayer: false,
        isParent: false,
        isGuest: false,
        guestTeam: '',
        isActive: true,
        sendWelcomeEmail: false,
      });
      setSelectedAgeGroupIds([]);
      setSelectedPlayerId(null);
      setSelectedChildPlayerIds([]);
    }
    setError(null);
  }, [user, isOpen, players]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setLoading(true);

    try {
      let userId = user?.id;
      const ageGroupIds = (formData.isAdmin || formData.isCoach) ? selectedAgeGroupIds : [];

      if (user) {
        const updateData: any = {
          name: formData.name,
          email: formData.email,
          isAdmin: formData.isAdmin,
          isCoach: formData.isCoach,
          isPlayer: formData.isPlayer,
          isParent: formData.isParent,
          isGuest: formData.isGuest,
          guestTeam: formData.isGuest ? formData.guestTeam.trim() : '',
          isActive: formData.isActive,
          ageGroupIds,
        };

        if (formData.password) {
          updateData.password = formData.password;
        }

        await usersAPI.update(user.id, updateData);
      } else {
        if (!formData.password) {
          setError('A jelszó megadása kötelező új felhasználó létrehozásánál');
          setLoading(false);
          return;
        }

        const newUser = await usersAPI.create({
          name: formData.name,
          email: formData.email,
          password: formData.password,
          isAdmin: formData.isAdmin,
          isCoach: formData.isCoach,
          isPlayer: formData.isPlayer,
          isParent: formData.isParent,
          isGuest: formData.isGuest,
          guestTeam: formData.isGuest ? formData.guestTeam.trim() : undefined,
          ageGroupIds,
          sendWelcomeEmail: formData.sendWelcomeEmail,
        });
        userId = newUser.id;
      }

      // A Player és a Parent képesség FÜGGETLENÜL kezelendő - egy usernek
      // egyszerre mindkettő is lehet (pl. saját maga is játékos, és emellett
      // egy másik gyerek szülője).
      if (userId) {
        if (formData.isPlayer) {
          // Előbb levesszük erről a userről minden korábbi, most már nem
          // kiválasztott saját-játékos linket.
          for (const player of players) {
            if (player.userId === userId && player.id !== selectedPlayerId) {
              await playersAPI.update(player.id, { clearUserId: true });
            }
          }
          if (selectedPlayerId) {
            await playersAPI.update(selectedPlayerId, { userId });
          }
        } else {
          // A Player checkbox kikapcsolva - a korábbi saját-játékos linket
          // is bontjuk, különben árván maradna rajta a felhasználó.
          for (const player of players) {
            if (player.userId === userId) {
              await playersAPI.update(player.id, { clearUserId: true });
            }
          }
        }

        if (formData.isParent) {
          for (const player of players) {
            const isCurrentlyChild = selectedChildPlayerIds.includes(player.id);
            const wasFirstParent = player.parentUserId === userId;
            const wasSecondParent = player.secondParentUserId === userId;

            if (isCurrentlyChild && !wasFirstParent && !wasSecondParent) {
              if (!player.parentUserId) {
                await playersAPI.update(player.id, { parentUserId: userId });
              } else if (!player.secondParentUserId) {
                await playersAPI.update(player.id, { secondParentUserId: userId });
              }
            } else if (!isCurrentlyChild && (wasFirstParent || wasSecondParent)) {
              if (wasFirstParent) {
                await playersAPI.update(player.id, { clearParentUserId: true });
              } else if (wasSecondParent) {
                await playersAPI.update(player.id, { clearSecondParentUserId: true });
              }
            }
          }
        } else {
          // A Parent checkbox kikapcsolva - minden szülői linket bontunk.
          for (const player of players) {
            if (player.parentUserId === userId) {
              await playersAPI.update(player.id, { clearParentUserId: true });
            } else if (player.secondParentUserId === userId) {
              await playersAPI.update(player.id, { clearSecondParentUserId: true });
            }
          }
        }
      }

      onSuccess();
    } catch (err: any) {
      setError(getErrorMessage(err, 'Hiba történt a mentés során'));
    } finally {
      setLoading(false);
    }
  };

  const toggleCapability = (key: 'isAdmin' | 'isCoach' | 'isPlayer' | 'isParent' | 'isGuest', checked: boolean) => {
    setFormData(prev => ({ ...prev, [key]: checked }));

    if (key === 'isPlayer' && !checked) {
      setSelectedPlayerId(null);
    }
    if (key === 'isParent' && !checked) {
      setSelectedChildPlayerIds([]);
    }
    if (key === 'isGuest' && !checked) {
      setFormData(prev => ({ ...prev, guestTeam: '' }));
    }
  };

  const handleAgeGroupToggle = (ageGroupId: number) => {
    setSelectedAgeGroupIds(prev =>
      prev.includes(ageGroupId)
        ? prev.filter(id => id !== ageGroupId)
        : [...prev, ageGroupId]
    );
  };

  const handleSelectAllAgeGroups = () => {
    if (selectedAgeGroupIds.length === availableAgeGroups.length) {
      setSelectedAgeGroupIds([]);
    } else {
      setSelectedAgeGroupIds(availableAgeGroups.map(ag => ag.id));
    }
  };

  const generateRandomPassword = () => {
    // Kriptográfiai véletlen; addig generálunk, amíg betű és szám is van benne
    const chars = 'abcdefghijkmnpqrstuvwxyzABCDEFGHJKLMNPQRSTUVWXYZ23456789';
    let password = '';
    do {
      const bytes = crypto.getRandomValues(new Uint8Array(12));
      password = Array.from(bytes, (b) => chars[b % chars.length]).join('');
    } while (validatePassword(password));
    setFormData({ ...formData, password });
  };

  const hasAnyCapability = formData.isAdmin || formData.isCoach || formData.isPlayer || formData.isParent || formData.isGuest;
  const isStaffCapable = formData.isAdmin || formData.isCoach;

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={user ? 'Felhasználó szerkesztése' : 'Új felhasználó'}
    >
      <form onSubmit={handleSubmit} className="space-y-4">
        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">
            {error}
          </div>
        )}

        <div>
          <label htmlFor="name" className="block text-sm font-medium text-gray-700">
            Név *
          </label>
          <input
            type="text"
            id="name"
            required
            value={formData.name}
            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
            className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm"
          />
        </div>

        <div>
          <label htmlFor="email" className="block text-sm font-medium text-gray-700">
            Email *
          </label>
          <input
            type="email"
            id="email"
            required
            value={formData.email}
            onChange={(e) => setFormData({ ...formData, email: e.target.value })}
            className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm"
          />
        </div>

        <div>
          <label htmlFor="password" className="block text-sm font-medium text-gray-700">
            Jelszó {user ? '(hagyd üresen, ha nem szeretnéd megváltoztatni)' : '*'}
          </label>
          <div className="mt-1 flex gap-2">
            <input
              type="text"
              id="password"
              required={!user}
              minLength={PASSWORD_MIN_LENGTH}
              value={formData.password}
              onChange={(e) => setFormData({ ...formData, password: e.target.value })}
              className="block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm"
            />
            <button
              type="button"
              onClick={generateRandomPassword}
              className="px-3 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 whitespace-nowrap"
            >
              Generálás
            </button>
          </div>
        </div>

        {!user && (
          <div>
            <label className="flex items-center">
              <input
                type="checkbox"
                checked={formData.sendWelcomeEmail}
                onChange={(e) => setFormData({ ...formData, sendWelcomeEmail: e.target.checked })}
                className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
              />
              <span className="ml-2 text-sm text-gray-700">Jelszóbeállító link küldése emailben</span>
            </label>
            <p className="mt-1 text-xs text-gray-500">
              A felhasználó egy 7 napig érvényes linket kap, amellyel maga állít be jelszót. A jelszót nem küldjük el emailben.
            </p>
          </div>
        )}

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Szerepkörök *
          </label>
          <p className="mb-2 text-xs text-gray-500">
            Egyszerre több is bepipálható - pl. egy felnőtt játékos, aki egyben edző is.
          </p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            <label className="flex items-center">
              <input
                type="checkbox"
                checked={formData.isAdmin}
                onChange={(e) => toggleCapability('isAdmin', e.target.checked)}
                className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
              />
              <span className="ml-2 text-sm text-gray-700">Admin</span>
            </label>
            <label className="flex items-center">
              <input
                type="checkbox"
                checked={formData.isCoach}
                onChange={(e) => toggleCapability('isCoach', e.target.checked)}
                className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
              />
              <span className="ml-2 text-sm text-gray-700">Edző</span>
            </label>
            <label className="flex items-center">
              <input
                type="checkbox"
                checked={formData.isPlayer}
                onChange={(e) => toggleCapability('isPlayer', e.target.checked)}
                className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
              />
              <span className="ml-2 text-sm text-gray-700">Játékos</span>
            </label>
            <label className="flex items-center">
              <input
                type="checkbox"
                checked={formData.isParent}
                onChange={(e) => toggleCapability('isParent', e.target.checked)}
                className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
              />
              <span className="ml-2 text-sm text-gray-700">Szülő</span>
            </label>
            <label className="flex items-center">
              <input
                type="checkbox"
                checked={formData.isGuest}
                onChange={(e) => toggleCapability('isGuest', e.target.checked)}
                className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
              />
              <span className="ml-2 text-sm text-gray-700">Vendég</span>
            </label>
          </div>

          {formData.isGuest && (
            <div className="mt-3">
              <label htmlFor="guestTeam" className="block text-sm font-medium text-gray-700 mb-1">
                Külsős csapat neve
              </label>
              <input
                type="text"
                id="guestTeam"
                name="guestTeam"
                maxLength={100}
                value={formData.guestTeam}
                onChange={(e) => setFormData(prev => ({ ...prev, guestTeam: e.target.value }))}
                placeholder="pl. Szomszédvár FC"
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
              />
              <p className="mt-1 text-xs text-gray-500">
                Üresen hagyva belső kiszolgáló (gondnok, takarító, kitmanager). A vendég a
                klubnaptárat nézheti, de nem szerkeszthet semmit.
              </p>
            </div>
          )}
          {!hasAnyCapability && (
            <p className="mt-1 text-xs text-amber-600">Válassz legalább egy szerepkört.</p>
          )}
        </div>

        {isStaffCapable && (
          <div>
            <div className="flex items-center justify-between mb-2">
              <label className="block text-sm font-medium text-gray-700">
                Korosztályok
              </label>
              <button
                type="button"
                onClick={handleSelectAllAgeGroups}
                className="text-xs text-primary-600 hover:text-primary-800"
                disabled={loadingAgeGroups}
              >
                {selectedAgeGroupIds.length === availableAgeGroups.length ? 'Összes törlése' : 'Összes kijelölése'}
              </button>
            </div>
            {loadingAgeGroups ? (
              <p className="text-sm text-gray-500">Betöltés...</p>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-4 gap-2 max-h-32 overflow-y-auto border border-gray-200 rounded-md p-2">
                {availableAgeGroups.map(ageGroup => (
                  <label key={ageGroup.id} className="flex items-center">
                    <input
                      type="checkbox"
                      checked={selectedAgeGroupIds.includes(ageGroup.id)}
                      onChange={() => handleAgeGroupToggle(ageGroup.id)}
                      className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                    />
                    <span className="ml-1 text-sm text-gray-700">{ageGroup.name}</span>
                  </label>
                ))}
              </div>
            )}
            <p className="mt-1 text-xs text-gray-500">
              {formData.isAdmin
                ? 'Admin: alapértelmezetten minden korosztályhoz hozzáfér. Válaszd ki, ha korlátozni szeretnéd.'
                : 'Edző: válaszd ki, mely korosztályokhoz tartozó adatokat láthatja. Ha nincs kijelölve, minden korosztályt lát.'}
            </p>
          </div>
        )}

        {formData.isPlayer && (
          <div>
            <label htmlFor="playerId" className="block text-sm font-medium text-gray-700">
              Saját játékos profil
            </label>
            {loadingPlayers ? (
              <p className="mt-1 text-sm text-gray-500">Betöltés...</p>
            ) : (
              <div className="mt-1">
                <PlayerPicker
                  id="playerId"
                  mode="single"
                  players={players}
                  selectedIds={selectedPlayerId ? [selectedPlayerId] : []}
                  onChange={(ids) => setSelectedPlayerId(ids[0] ?? null)}
                  disabledReason={(p) => (p.userId && p.userId !== user?.id ? 'már másik fiókhoz tartozik' : null)}
                />
              </div>
            )}
            <p className="mt-1 text-xs text-gray-500">
              Válaszd ki, melyik játékos profil tartozik ehhez a felhasználóhoz.
            </p>
          </div>
        )}

        {formData.isParent && (
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Gyermekek (játékosok)
            </label>
            {loadingPlayers ? (
              <p className="text-sm text-gray-500">Betöltés...</p>
            ) : players.length === 0 ? (
              <p className="text-sm text-gray-500">Nincsenek játékosok a rendszerben.</p>
            ) : (
              <PlayerPicker
                players={players}
                selectedIds={selectedChildPlayerIds}
                onChange={setSelectedChildPlayerIds}
                disabledReason={(p) =>
                  p.parentUserId && p.parentUserId !== user?.id && p.secondParentUserId && p.secondParentUserId !== user?.id
                    ? 'már van 2 szülője'
                    : null
                }
              />
            )}
            <p className="mt-1 text-xs text-gray-500">
              Válaszd ki, mely játékosoknak a szülője ez a felhasználó.
            </p>
          </div>
        )}

        {user && (
          <div>
            <label className="flex items-center">
              <input
                type="checkbox"
                checked={formData.isActive}
                onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })}
                className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
              />
              <span className="ml-2 text-sm text-gray-700">Aktív felhasználó</span>
            </label>
          </div>
        )}

        <div className="flex flex-col-reverse gap-2 pt-4 md:flex-row md:justify-end md:gap-3">
          <button
            type="button"
            onClick={onClose}
            disabled={loading}
            className="w-full md:w-auto px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
          >
            Mégse
          </button>
          <button
            type="submit"
            disabled={loading || !hasAnyCapability}
            className="w-full md:w-auto px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:bg-gray-400"
          >
            {loading ? 'Mentés...' : user ? 'Mentés' : 'Létrehozás'}
          </button>
        </div>
      </form>
    </Modal>
  );
};

export default UserForm;
